// [PURPOSE]: Centralized registry of SRD race traits with mechanical effects.
// [WHY]: srdCharacterData.js stores race features as strings only ('Darkvision',
//   'Hellish Resistance', 'Dwarven Resilience', etc.). This file maps each named
//   race trait to its effects array so the effectRegistry can register and consume
//   them uniformly — mirrors srdClassFeatures.js for the racial side of the
//   effect system.
// [INTEGRATION]: useRaceFeatureEffects hook reads this on race/subrace change
//   and registers effects with prefix 'race:<RaceName>:<TraitName>' (or
//   'race:<RaceName>:<Subrace>:<TraitName>' for subrace-specific traits).
// [Phase 4b bucket 4 — APR 2026]: Adds darkvision senses, racial resistances,
//   advantage grants, and proficiency grants that the SRD race string-list
//   features did not previously wire mechanically.

/**
 * Race feature effect registry.
 *
 * Schema:
 *   SRD_RACE_FEATURES[raceName] = {
 *     base: TraitMap,         // Traits granted by the base race regardless of subrace
 *     subraces?: {            // Optional subrace-specific traits (merged on top of base)
 *       [subraceName]: TraitMap
 *     }
 *   }
 *   TraitMap = { [traitName]: { effects: Effect[], notes?: string } }
 *
 * Effects use the unified schema consumed by effectRegistry.
 *
 * [DESIGN NOTE]: Effects on properties already applied directly to playerStats by
 * useRaceEffects.js (e.g., speed, hitPointBonus, basic damageResistance arrays)
 * are NOT duplicated here — this registry only adds the missing/incomplete wiring:
 *   - sense effects (darkvision range — surfaces in SensesMovementPanel)
 *   - resistance effects (so getDamageResistances picks them up via registry)
 *   - advantage effects (so hasAdvantageOnCheck works for racial save advantage)
 *   - proficiency effects (so getGrantedProficiencyLevel works for racial skill grants)
 *   - speed override (Wood Elf 35ft — already applied directly, registered here for
 *     parity with custom races that use registry-driven speed effects)
 */
export const SRD_RACE_FEATURES = {
  Dwarf: {
    base: {
      'Darkvision': {
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 60 }
        ],
      },
      'Dwarven Resilience': {
        effects: [
          { type: 'resistance', operation: 'grant', value: 'poison' },
          { type: 'advantage', operation: 'advantage', target: 'save:poison' }
        ],
        notes: 'PHB p.20 — Resistance to poison damage and advantage on saves vs poison.',
      },
    },
    subraces: {
      'Mountain Dwarf': {
        'Dwarven Armor Training': {
          effects: [
            { type: 'proficiency', operation: 'grant', value: 'armor:light' },
            { type: 'proficiency', operation: 'grant', value: 'armor:medium' }
          ],
          notes: 'PHB p.20 — Mountain Dwarves gain proficiency with light and medium armor.',
        },
      },
    },
  },

  Elf: {
    base: {
      'Darkvision': {
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 60 }
        ],
      },
      'Keen Senses': {
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'skill:perception', proficiencyLevel: 1 }
        ],
        notes: 'PHB p.23 — Proficiency in the Perception skill.',
      },
      'Fey Ancestry': {
        effects: [
          { type: 'advantage', operation: 'advantage', target: 'save:charm' }
        ],
        notes: 'PHB p.23 — Advantage on saving throws against being charmed; magic cannot put you to sleep (modeled separately as a feature flag).',
      },
    },
    subraces: {
      'Wood Elf': {
        'Fleet of Foot': {
          effects: [
            { type: 'speed', operation: 'set', value: 35 }
          ],
          notes: 'PHB p.24 — Wood Elf base walking speed 35.',
        },
      },
      'Dark Elf (Drow)': {
        'Superior Darkvision': {
          effects: [
            { type: 'sense', operation: 'grant', target: 'darkvision', value: 120 }
          ],
          notes: 'PHB p.24 — Drow darkvision range is 120 ft (overrides 60).',
        },
      },
    },
  },

  Halfling: {
    base: {
      'Brave': {
        effects: [
          { type: 'advantage', operation: 'advantage', target: 'save:frightened' }
        ],
        notes: 'PHB p.28 — Advantage on saving throws against being frightened.',
      },
    },
    subraces: {
      'Stout': {
        'Stout Resilience': {
          effects: [
            { type: 'resistance', operation: 'grant', value: 'poison' },
            { type: 'advantage', operation: 'advantage', target: 'save:poison' }
          ],
          notes: 'PHB p.28 — Resistance to poison damage and advantage on saves vs poison.',
        },
      },
    },
  },

  Gnome: {
    base: {
      'Darkvision': {
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 60 }
        ],
      },
      'Gnome Cunning': {
        effects: [
          { type: 'advantage', operation: 'advantage', target: 'save:int_magic' },
          { type: 'advantage', operation: 'advantage', target: 'save:wis_magic' },
          { type: 'advantage', operation: 'advantage', target: 'save:cha_magic' }
        ],
        notes: 'PHB p.37 — Advantage on Intelligence, Wisdom, and Charisma saves vs magic.',
      },
    },
  },

  'Half-Elf': {
    base: {
      'Darkvision': {
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 60 }
        ],
      },
      'Fey Ancestry': {
        effects: [
          { type: 'advantage', operation: 'advantage', target: 'save:charm' }
        ],
        notes: 'PHB p.39 — Advantage on saves vs charm; magic cannot put you to sleep (modeled separately).',
      },
    },
  },

  'Half-Orc': {
    base: {
      'Darkvision': {
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 60 }
        ],
      },
      'Menacing': {
        effects: [
          { type: 'proficiency', operation: 'grant', value: 'skill:intimidation', proficiencyLevel: 1 }
        ],
        notes: 'PHB p.41 — Proficiency in the Intimidation skill.',
      },
      'Savage Attacks': {
        effects: [
          { type: 'feature', operation: 'grant', value: 'savage_attacks' }
        ],
        notes: 'PHB p.41 — On a melee weapon crit, roll one additional weapon damage die. Surfaced via hasFeatureFlag for combat UI.',
      },
    },
  },

  Tiefling: {
    base: {
      'Darkvision': {
        effects: [
          { type: 'sense', operation: 'grant', target: 'darkvision', value: 60 }
        ],
      },
      'Hellish Resistance': {
        effects: [
          { type: 'resistance', operation: 'grant', value: 'fire' }
        ],
        notes: 'PHB p.43 — Resistance to fire damage. Already implemented via raceData.damageResistance — registered here for breakdown attribution.',
      },
      // [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Infernal Legacy reaction (Hellish
      // Rebuke). Registered unconditionally — race traits carry no minLevel
      // (useRaceFeatureEffects.js:46) — but LEVEL-GATED at read time by
      // effectCalculator.getReactionTriggers via the baked `minLevel` (mirrors
      // the raceResources 'infernal_legacy_hellish_rebuke' pool def minLevel:3).
      // The save DC (8 + CHA mod + prof) is computed at INVOKE time, so rollSpec
      // carries `dcAbility:'charisma'`, NOT a static dc. Charge tracking + recharge
      // stay on the raceResources pool; the StatsPanel Reactions section is the
      // invoke surface. Trace:
      // .claude/findings/2026-06-03-datamodel-hellish-rebuke-reactiontrigger-h3rb1.md
      'Infernal Legacy (Hellish Rebuke)': {
        effects: [
          {
            type: 'reactionTrigger',
            operation: 'grant',
            value: {
              actionName: 'Hellish Rebuke',
              description: 'Reaction when you take damage: 3d10 fire, DEX save for half. 1/long rest, no spell slot. PHB p.43.',
              raceKey: 'Tiefling',
              attachedToResource: 'infernal_legacy_hellish_rebuke',
              minLevel: 3,   // mirrors raceResources pool def (race traits carry no minLevel of their own)
              maxUses: 1,    // mirrors maxCalculation:'fixed:1' — generalize via 1H-G-bw-S3a-maxcalc-general
              cost: 1,
              rollSpec: {
                rolls: [{ dice: '3d10', label: 'fire damage' }],
                save: { ability: 'dexterity', dcAbility: 'charisma', halfOnSave: true },
                targetMode: 'enemy',
              },
            },
          },
        ],
        notes: 'PHB p.43 — Tiefling casts Hellish Rebuke as a 2nd-level spell (3d10) once per long rest at L3+. Invoked from the StatsPanel Reactions section.',
      },
    },
  },

  Dragonborn: {
    base: {
      // Breath weapon resistance is per-subrace (mapped from subrace damageType field).
      // Subrace entries below grant the matching elemental resistance.
    },
    subraces: {
      'Black': {
        'Draconic Resistance (Acid)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'acid' }],
          notes: 'PHB p.34 — Black Dragonborn have resistance to acid damage.',
        },
      },
      'Blue': {
        'Draconic Resistance (Lightning)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'lightning' }],
          notes: 'PHB p.34 — Blue Dragonborn have resistance to lightning damage.',
        },
      },
      'Brass': {
        'Draconic Resistance (Fire)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'fire' }],
          notes: 'PHB p.34 — Brass Dragonborn have resistance to fire damage.',
        },
      },
      'Bronze': {
        'Draconic Resistance (Lightning)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'lightning' }],
          notes: 'PHB p.34 — Bronze Dragonborn have resistance to lightning damage.',
        },
      },
      'Copper': {
        'Draconic Resistance (Acid)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'acid' }],
          notes: 'PHB p.34 — Copper Dragonborn have resistance to acid damage.',
        },
      },
      'Gold': {
        'Draconic Resistance (Fire)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'fire' }],
          notes: 'PHB p.34 — Gold Dragonborn have resistance to fire damage.',
        },
      },
      'Green': {
        'Draconic Resistance (Poison)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'poison' }],
          notes: 'PHB p.34 — Green Dragonborn have resistance to poison damage.',
        },
      },
      'Red': {
        'Draconic Resistance (Fire)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'fire' }],
          notes: 'PHB p.34 — Red Dragonborn have resistance to fire damage.',
        },
      },
      'Silver': {
        'Draconic Resistance (Cold)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'cold' }],
          notes: 'PHB p.34 — Silver Dragonborn have resistance to cold damage.',
        },
      },
      'White': {
        'Draconic Resistance (Cold)': {
          effects: [{ type: 'resistance', operation: 'grant', value: 'cold' }],
          notes: 'PHB p.34 — White Dragonborn have resistance to cold damage.',
        },
      },
    },
  },
};

// Freeze each top-level race entry so callers can't mutate the registry at runtime.
Object.values(SRD_RACE_FEATURES).forEach(raceEntry => {
  if (raceEntry.base) Object.freeze(raceEntry.base);
  if (raceEntry.subraces) {
    Object.values(raceEntry.subraces).forEach(subEntry => Object.freeze(subEntry));
    Object.freeze(raceEntry.subraces);
  }
  Object.freeze(raceEntry);
});
Object.freeze(SRD_RACE_FEATURES);

/**
 * Returns the array of [traitName, traitData] entries that the character has
 * unlocked for a given race + subrace.
 *
 * Subrace-specific traits are merged on top of base race traits (subrace traits
 * with the same name override the base). This matches contentResolver.getRaceData
 * subrace merge semantics.
 *
 * @param {string} raceName
 * @param {string|null} subraceName
 * @returns {Array<[string, object]>}
 */
export function getActiveRaceFeatures(raceName, subraceName = null) {
  if (!raceName) return [];
  const raceEntry = SRD_RACE_FEATURES[raceName];
  if (!raceEntry) return [];

  const merged = { ...(raceEntry.base || {}) };

  if (subraceName && raceEntry.subraces && raceEntry.subraces[subraceName]) {
    const subTraits = raceEntry.subraces[subraceName];
    for (const [traitName, traitData] of Object.entries(subTraits)) {
      merged[traitName] = traitData;
    }
  }

  return Object.entries(merged).filter(([_, trait]) => trait.effects && trait.effects.length > 0);
}

export default SRD_RACE_FEATURES;
